import { FC, PropsWithChildren, ReactNode } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogProps, DialogTitle, IconButton } from "@mui/material";
import { FormattedMessage } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import { onCloseUnlessEscape } from "../../utils/dialogUtils";

interface IPropsBase {
    confirmOptions: {
        isShown: boolean;
        text: string | ReactNode;
        title: string | ReactNode;
        color?: string;
    };
    maxWidth?: DialogProps["maxWidth"];
    handleAccept(e): void;
    disableAccept?: boolean;
    handleCancel(e): void;
    disableCancel?: boolean;
}

interface CustomBtnIProps extends IPropsBase {
    yesText: string | ReactNode;
    noText: string | ReactNode;
}

interface StandardBtnIProps extends IPropsBase {
    yesText?: never;
    noText?: never;
}

export interface ConfirmOptions {
    isShown: boolean;
    text: string | ReactNode;
    title: string | ReactNode;
}

export const initialConfirmOptions = {
    isShown: false,
    text: "",
    title: "",
};

const ConfirmDialog: FC<PropsWithChildren<StandardBtnIProps | CustomBtnIProps>> = ({
    confirmOptions,
    handleAccept,
    handleCancel,
    noText,
    yesText,
    maxWidth,
    disableAccept,
    disableCancel,
    children,
}) => {
    return (
        <Dialog
            open={confirmOptions.isShown}
            onClose={onCloseUnlessEscape(() => handleCancel(undefined))}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            maxWidth={maxWidth}
            fullWidth
        >
            <DialogTitle color={confirmOptions.color} id="confirm-dialog-title" sx={{ maxWidth: "90%" }}>
                {confirmOptions.title}
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText component="div" color={confirmOptions?.color || "softBlack.main"}>
                    {confirmOptions.text}
                </DialogContentText>
                {children && <div style={{ minHeight: "40vh", boxSizing: "border-box" }}>{children}</div>}
            </DialogContent>
            <DialogActions sx={{ position: "absolute", right: "0px", top: ".8%" }}>
                <IconButton onClick={handleCancel} aria-label="Close confirmation dialog">
                    <CloseIcon sx={(theme) => ({ color: theme.palette.softBlack?.main })} />
                </IconButton>
            </DialogActions>
            <DialogActions>
                <Button onClick={handleCancel} disabled={disableCancel}>
                    {noText || <FormattedMessage id="confirm-dialog.label.decline" defaultMessage="CANCEL" />}
                </Button>
                <Button onClick={handleAccept} disabled={disableAccept}>
                    {yesText || <FormattedMessage id="common.button-label.ok" defaultMessage="OK" />}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
